import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components/macro';
import {Checkbox} from '@/web-components';
import {
	isFixedPriceDeliverableInvoiceable,
	buildLineForFixedPriceDeliverable,
	remainingToInvoice,
} from '../../../../components/insights/invoicing/line-generation/invoicing_generate_lines_fixedpricedeliverable';
import {
	GroupingBoxSection,
	GroupingBoxHeadline,
	GroupingBoxSectionContent,
} from '../../../../styles/v2/invoice_modals_v2_styled';
import Util from '../../../../forecast-app/shared/util/util';
import {hoverRevealScrollbar} from '../../../../components/timesheets/team-timesheet/scrollbar-mixins';

// Cap the visible checklist at 3 rows before scrolling, so a project with many priced
// Deliverables doesn't stretch this column taller than its "Billable Hours"/"Include Expenses"
// siblings in the grouping box. Uses a plain native overflow-y scroll area (styled via
// hoverRevealScrollbar) rather than CustomScrollDiv/react-custom-scrollbars — that library hides
// the native scrollbar with a negative-margin trick that doesn't fully suppress it in every
// browser/OS scrollbar theme, producing a visible native scrollbar alongside its custom one.
const DELIVERABLE_ROW_HEIGHT_PX = 36;
const MAX_VISIBLE_DELIVERABLE_ROWS = 3;

const DeliverablesScrollArea = styled.div`
	overflow-y: auto;
	max-height: ${DELIVERABLE_ROW_HEIGHT_PX * MAX_VISIBLE_DELIVERABLE_ROWS}px;
	${hoverRevealScrollbar}
`;

// Widen the column to fit the longest Deliverable name so it doesn't get clipped under the
// vertical scrollbar. Measured via canvas (same technique as input_title.js's calculateWidth)
// rather than a per-character estimate, since font metrics aren't monospace.
// ROW_CHROME_PX covers the checkbox, the two flex gaps, the formatted price, and the scrollbar gutter.
// MAX_SECTION_WIDTH_PX caps how far the column can grow — beyond that, the name's own
// overflow/ellipsis truncation (below) takes over instead of stretching the whole modal wider.
const DELIVERABLE_NAME_FONT = '400 14px neue-haas-grotesk-text, sans-serif';
const ROW_CHROME_PX = 120;
const MIN_SECTION_WIDTH_PX = 200;
const MAX_SECTION_WIDTH_PX = 280;
const NAME_CHAR_WIDTH_FALLBACK_PX = 8; // used only if canvas measurement is unavailable

const measureTextWidth = (text, font) => {
	if (typeof document === 'undefined') {
		return text.length * NAME_CHAR_WIDTH_FALLBACK_PX;
	}
	const context = document.createElement('canvas').getContext('2d');
	context.font = font;
	return context.measureText(text).width;
};

const FixedPriceDeliverablesSection = ({projects, invoice, company, invoiceLines, setInvoiceLines}) => {
	const intl = useIntl();

	const candidates = projects.flatMap(project =>
		(project.deliverables ? project.deliverables.edges.map(edge => edge.node) : [])
			.filter(deliverable => isFixedPriceDeliverableInvoiceable(deliverable, project))
			.map(deliverable => ({project, deliverable}))
	);

	if (candidates.length === 0) {
		return null;
	}

	const longestNameWidth = candidates.reduce(
		(max, {deliverable}) => Math.max(max, measureTextWidth(deliverable.name, DELIVERABLE_NAME_FONT)),
		0
	);
	const sectionWidth = Math.min(MAX_SECTION_WIDTH_PX, Math.max(MIN_SECTION_WIDTH_PX, longestNameWidth + ROW_CHROME_PX));

	const isSelected = deliverableId =>
		invoiceLines.some(line => line.deliverableIds && line.deliverableIds[0] === deliverableId);

	const toggleDeliverable = (project, deliverable) => {
		if (isSelected(deliverable.id)) {
			setInvoiceLines(invoiceLines.filter(line => !(line.deliverableIds && line.deliverableIds[0] === deliverable.id)));
		} else {
			setInvoiceLines([...invoiceLines, buildLineForFixedPriceDeliverable(deliverable, project, invoice, company)]);
		}
	};

	return (
		<GroupingBoxSection data-cy={'fixed-price-deliverables-section'} style={{width: sectionWidth}}>
			<GroupingBoxHeadline>{intl.formatMessage({id: 'invoicing.fixed_price_deliverables'})}</GroupingBoxHeadline>
			<GroupingBoxSectionContent style={{flexDirection: 'column'}}>
				<DeliverablesScrollArea>
					{candidates.map(({project, deliverable}) => {
						const currency = project.rateCard ? project.rateCard.currency : company.currency;
						return (
							<label
								key={deliverable.id}
								style={{display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer'}}
							>
								<Checkbox
									checked={isSelected(deliverable.id)}
									onChange={() => toggleDeliverable(project, deliverable)}
									cy={`fixed-price-deliverable-checkbox-${deliverable.id}`}
								/>
								<span
									title={deliverable.name}
									style={{
										flex: 1,
										minWidth: 0,
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									}}
								>
									{deliverable.name}
								</span>
								<span style={{flexShrink: 0}}>
									{Util.GetFormattedCurrencySymbol(currency, remainingToInvoice(deliverable))}
								</span>
							</label>
						);
					})}
				</DeliverablesScrollArea>
			</GroupingBoxSectionContent>
		</GroupingBoxSection>
	);
};

export default FixedPriceDeliverablesSection;
